import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Flex,
  Heading,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Text,
  useToast,
  Spinner,
  Badge,
  Tabs,
  TabList,
  Tab,
  Image,
  Link,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import {
  SearchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EditIcon,
  AddIcon,
  CheckCircleIcon,
} from "@chakra-ui/icons";
import { supabase } from "../../lib/supabaseClient";
import EditAlbumModal from "../../components/admin/EditAlbumModal";
import EditTrackModal from "../../components/admin/EditTrackModal";
import EditMusicianModal from "../../components/admin/EditMusicianModal";
import MusicImportModal from "../../components/admin/MusicImportModal";

const PAGE_SIZE = 10;

const AdminMusic = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [data, setData] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const toast = useToast();
  const [deletingId, setDeletingId] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const cancelRef = useRef();

  const getTableName = () => {
    switch (activeTab) {
      case 0:
        return "album";
      case 1:
        return "track";
      case 2:
        return "musician";
      default:
        return "album";
    }
  };

  const getSearchFields = () => {
    switch (activeTab) {
      case 0:
        return [
          "name_album_en",
          "name_album_kr",
          "code_album",
          "musician",
        ];
      case 1:
        return ["track_name_kr", "isrc", "track_code", "album", "musician"];
      case 2:
        return ["name_musician", "name_korean", "name_english", "user"];
      default:
        return [];
    }
  };

  const getImportDisplayColumns = () => {
    switch (activeTab) {
      case 0:
        return [
          { key: "name_album_kr", label: "앨범명(KR)" },
          { key: "name_album_en", label: "앨범명(EN)" },
          { key: "code_album", label: "앨범코드" },
          { key: "musician", label: "뮤지션" },
          { key: "published_at", label: "발매일" },
          { key: "user", label: "사용자" },
        ];
      case 1:
        return [
          { key: "track_name_kr", label: "트랙명" },
          { key: "isrc", label: "ISRC" },
          { key: "track_code", label: "트랙코드" },
          { key: "album", label: "앨범명" },
          { key: "musician", label: "뮤지션" },
        ];
      case 2:
        return [
          { key: "name_musician", label: "뮤지션명" },
          { key: "name_korean", label: "한국어 이름" },
          { key: "name_english", label: "영어 이름" },
          { key: "user", label: "사용자" },
        ];
      default:
        return [];
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    const tableName = getTableName();
    const searchFields = getSearchFields();

    let query = supabase.from(tableName).select("*", { count: "exact" });

    // 검색 조건
    if (searchQuery) {
      const searchConditions = searchFields
        .map((field) => `${field}.ilike.%${searchQuery}%`)
        .join(",");
      query = query.or(searchConditions);
    }

    const {
      data: result,
      error,
      count,
    } = await query
      .order("created_date", { ascending: false })
      .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

    if (error) {
      toast({
        title: "데이터 조회 실패",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setData([]);
    } else {
      setData(result);
      setTotalPages(Math.ceil(count / PAGE_SIZE));
    }
    setLoading(false);
  }, [page, searchQuery, activeTab, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearchTrigger = () => {
    setPage(1);
    setSearchQuery(searchTerm);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearchTrigger();
    }
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setIsEditModalOpen(true);
  };

  const handleNewItem = () => {
    setIsRegisterModalOpen(true);
  };

  const handleDelete = async (item) => {
    if (!item.id) return;
    setDeletingId(item.id);
    try {
      const tableName = getTableName();
      const { error } = await supabase
        .from(tableName)
        .delete()
        .eq("id", item.id);
      if (error) throw error;
      toast({
        title: "삭제 완료",
        status: "success",
        duration: 1500,
        isClosable: true,
      });
      fetchData();
    } catch (error) {
      toast({
        title: "삭제 실패",
        description: error.message,
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setDeletingId(null);
      setDeletingItem(null);
    }
  };

  const getTabTitle = () => {
    switch (activeTab) {
      case 0:
        return "앨범";
      case 1:
        return "트랙";
      case 2:
        return "뮤지션";
      default:
        return "앨범";
    }
  };

  const getColumns = () => {
    switch (activeTab) {
      case 0: // album
        return [
          { key: "cover", label: "커버" },
          { key: "name_album_en", label: "앨범명(EN)" },
          { key: "name_album_kr", label: "앨범명(KR)" },
          { key: "code_album", label: "앨범코드" },
          { key: "label", label: "레이블" },
          { key: "musician", label: "뮤지션" },
          { key: "status_musician", label: "상태" },
          { key: "registered_at", label: "등록일" },
          { key: "published_at", label: "발매일" },
        ];
      case 1: // track
        return [
          { key: "track_name_kr", label: "트랙명" },
          { key: "isrc", label: "ISRC" },
          { key: "track_code", label: "트랙 코드" },
          { key: "commission_rate", label: "수익률" },
          { key: "album", label: "앨범명" },
          { key: "musician", label: "뮤지션" },
          { key: "genre_depth4", label: "장르" },
          { key: "mp3_ar", label: "MP3" },
          { key: "wav_ar", label: "WAV" },
          { key: "created_date", label: "등록일" },
        ];
      case 2: // musician
        return [
          { key: "name_musician_kr", label: "뮤지션" },
          { key: "user", label: "사용자" },
          { key: "contracted_company", label: "소속사" },
          { key: "created_date", label: "등록일" },
        ];
      default:
        return [];
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return `${date.getFullYear()}. ${date.getMonth() + 1}. ${date.getDate()}.`;
  };

  const formatValue = (value, key) => {
    if (value === null || value === undefined) return "-";
    if (
      key === "created_date" ||
      key === "modified_date" ||
      key === "published_at" ||
      key === "registered_at"
    ) {
      return formatDate(value);
    }
    return String(value);
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading as="h1" size="lg">
          음악 관리
        </Heading>
        <Button
          colorScheme="blue"
          leftIcon={<AddIcon />}
          onClick={handleNewItem}
        >
          {getTabTitle()} 가져오기
        </Button>
      </Flex>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
        {/* 검색 */}
        <Flex mb={4}>
          <InputGroup maxW="300px">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder={`${getTabTitle()} 검색`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </InputGroup>
          <Button ml={2} onClick={handleSearchTrigger}>
            검색
          </Button>
        </Flex>

        {/* 탭 */}
        <Tabs index={activeTab} onChange={setActiveTab} mb={4}>
          <TabList>
            <Tab>앨범</Tab>
            <Tab>트랙</Tab>
            <Tab>뮤지션</Tab>
          </TabList>
        </Tabs>

        {loading ? (
          <Flex justify="center" p={10}>
            <Spinner />
          </Flex>
        ) : (
          <>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  {getColumns().map((column) => (
                    <Th key={column.key}>{column.label}</Th>
                  ))}
                  <Th>작업</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.map((item, index) => (
                  <Tr key={item.id || index}>
                    {getColumns().map((column) => (
                      <Td key={column.key}>
                        {(() => {
                          const value = item[column.key];
                          if (activeTab === 0 && column.key === "cover") {
                            return (
                              <Image
                                src={value || "/images/placeholder.png"}
                                alt="커버"
                                boxSize="40px"
                                borderRadius="md"
                                objectFit="cover"
                              />
                            );
                          }
                          if (
                            activeTab === 1 &&
                            (column.key === "mp3_ar" || column.key === "wav_ar")
                          ) {
                            return value ? (
                              <Link
                                href={value}
                                isExternal
                                color="blue.500"
                                _hover={{ textDecoration: "underline" }}
                              >
                                다운로드
                              </Link>
                            ) : (
                              "-"
                            );
                          }
                          if (
                            activeTab === 1 &&
                            column.key === "genre_depth4"
                          ) {
                            return value ? (
                              <Badge colorScheme="cyan">{value}</Badge>
                            ) : (
                              "-"
                            );
                          }
                          if (
                            activeTab === 2 &&
                            column.key === "name_musician_kr"
                          ) {
                            return (
                              <Box>
                                <Text fontWeight="medium">
                                  {item.name_musician_kr || item.name_musician}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                  {item.genre}
                                </Text>
                              </Box>
                            );
                          }
                          return (
                            <Text noOfLines={1}>
                              {formatValue(value, column.key)}
                            </Text>
                          );
                        })()}
                      </Td>
                    ))}
                    <Td>
                      <Button
                        size="xs"
                        leftIcon={<EditIcon />}
                        onClick={() => handleEdit(item)}
                        mr={2}
                      >
                        수정
                      </Button>
                      <Button
                        size="xs"
                        colorScheme="red"
                        variant="outline"
                        isLoading={deletingId === item.id}
                        onClick={() => setDeletingItem(item)}
                      >
                        삭제
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>

            <Flex justify="center" align="center" mt={6}>
              <IconButton
                icon={<ChevronLeftIcon />}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                isDisabled={page === 1}
                size="sm"
              />
              <Text mx={4} fontSize="sm">
                Page {page} of {totalPages}
              </Text>
              <IconButton
                icon={<ChevronRightIcon />}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                isDisabled={page === totalPages}
                size="sm"
              />
            </Flex>
          </>
        )}
      </Box>

      <MusicImportModal
        isOpen={isRegisterModalOpen}
        onClose={() => setIsRegisterModalOpen(false)}
        dataType={getTableName()}
        searchFields={getSearchFields()}
        displayColumns={getImportDisplayColumns()}
        onImportSuccess={() => fetchData()}
      />
      {activeTab === 0 && (
        <EditAlbumModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          album={selectedItem}
          onSave={() => {
            fetchData();
            setIsEditModalOpen(false);
          }}
        />
      )}
      {activeTab === 1 && (
        <EditTrackModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          track={selectedItem}
          onSave={() => {
            fetchData();
            setIsEditModalOpen(false);
          }}
        />
      )}
      {activeTab === 2 && (
        <EditMusicianModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          musician={selectedItem}
          onSave={() => {
            fetchData();
            setIsEditModalOpen(false);
          }}
        />
      )}

      {/* 삭제 확인 모달 */}
      <AlertDialog
        isOpen={!!deletingItem}
        leastDestructiveRef={cancelRef}
        onClose={() => setDeletingItem(null)}
      >
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            삭제 확인
          </AlertDialogHeader>
          <AlertDialogBody>
            정말로 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={() => setDeletingItem(null)}>
              취소
            </Button>
            <Button
              colorScheme="red"
              onClick={() => handleDelete(deletingItem)}
              ml={3}
              isLoading={deletingId === (deletingItem && deletingItem.id)}
            >
              삭제
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Box>
  );
};

export default AdminMusic;
